import { Dialog, DialogContent, DialogTitle, Box, Typography, Button, useTheme, useMediaQuery, Stack, Divider } from '@mui/material';
import { LockOutlined, WarningAmberRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router';
const ProfessionalDialog = styled(Dialog)(({ theme }) => ({
    zIndex: theme.zIndex.tooltip + 10,
    '& .MuiDialog-paper': {
        background: '#ffffff',
        borderRadius: '16px',
        maxWidth: '520px',
        width: '100%',
        boxShadow: '0px 20px 60px rgba(25, 118, 210, 0.15), 0px 0px 1px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: GRADIENT_PRIMARY
        }
    },
    '& .MuiBackdrop-root': {
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)'
    }
}));
// Modern banking colors with gradients
const BLUE_PRIMARY = '#1976d2';
const BLUE_DARK = '#1565c0';
const BLUE_ACCENT = '#42a5f5';
const GRADIENT_PRIMARY = 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)';
const GRADIENT_LIGHT = 'linear-gradient(135deg, #e3f2fd 0%, #f5f9ff 100%)';
// Modern icon container with gradient and animation
const IconContainer = styled(Box)(() => ({
    width: '88px',
    height: '88px',
    borderRadius: '50%',
    background: GRADIENT_LIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    border: `3px solid ${BLUE_ACCENT}`,
    position: 'relative',
    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.2)',
    transition: 'transform 0.3s ease',
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: '-3px',
        borderRadius: '50%',
        padding: '3px',
        background: GRADIENT_PRIMARY,
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        opacity: 0.6
    },
    '& svg': {
        fontSize: '44px',
        color: BLUE_PRIMARY,
        position: 'relative',
        zIndex: 1,
        filter: 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.2))'
    }
}));

// Stylish service badge
const ServiceBadge = styled(Box)(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    borderRadius: '12px',
    background: GRADIENT_LIGHT,
    border: `2px solid ${BLUE_ACCENT}`,
    marginBottom: '24px',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
        transition: 'left 0.5s ease'
    },
    '&:hover::before': {
        left: '100%'
    }
}));

// Warning box with modern design
const WarningBox = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '18px 20px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
    border: '2px solid #ffc107',
    textAlign: 'left',
    marginTop: '16px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(255, 193, 7, 0.15)',
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        background: 'linear-gradient(180deg, #ff9800 0%, #ffc107 100%)'
    }
}));

// Gradient button
const GradientButton = styled(Button)(() => ({
    background: GRADIENT_PRIMARY,
    color: '#fff',
    padding: '12px 32px',
    borderRadius: '12px',
    textTransform: 'none',
    fontSize: '15px',
    fontWeight: 600,
    minWidth: '160px',
    boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
        transition: 'left 0.5s ease'
    },
    '&:hover': {
        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
        transform: 'translateY(-2px)',
        '&::before': {
            left: '100%'
        }
    },
    '&:active': {
        transform: 'translateY(0px)'
    }
}));
export function ServiceRestrictionDialog({ open, serviceName }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const defaultMessage =
        'Access to this service has been temporarily restricted. Please complete recharge process to restore full functionality.';

    const handleConfirm = () => {
        navigate('/merchant-home');
    };
    return (
        <ProfessionalDialog
            open={open}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    m: isMobile ? 0 : 2,
                    borderRadius: isMobile ? 0 : '16px'
                }
            }}
        >
            <DialogTitle
                sx={{
                    position: 'relative',
                    pb: 1,
                    pt: 3,
                    px: 3,
                    borderBottom: 'none'
                }}
            ></DialogTitle>

            <DialogContent sx={{ p: 3, pt: 1, textAlign: 'center' }}>
                <IconContainer>
                    <LockOutlined />
                </IconContainer>

                <Typography
                    variant="h5"
                    sx={{
                        background: GRADIENT_PRIMARY,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 700,
                        mb: 2,
                        fontSize: '28px',
                        letterSpacing: '-0.5px'
                    }}
                >
                    Access Restricted
                </Typography>

                {serviceName && (
                    <ServiceBadge>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: BLUE_DARK,
                                fontWeight: 600,
                                fontSize: '15px',
                                position: 'relative',
                                zIndex: 1
                            }}
                        >
                            {serviceName}
                        </Typography>
                    </ServiceBadge>
                )}

                <WarningBox>
                    <WarningAmberRounded
                        sx={{
                            color: '#f57c00',
                            mt: 0.5,
                            fontSize: '26px',
                            flexShrink: 0
                        }}
                    />
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#856404',
                            lineHeight: 1.7,
                            flex: 1,
                            fontSize: '14px',
                            fontWeight: 500
                        }}
                    >
                        {defaultMessage}
                    </Typography>
                </WarningBox>

                <Divider
                    sx={{
                        my: 3,
                        borderColor: 'rgba(25, 118, 210, 0.1)'
                    }}
                />

                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                    <GradientButton onClick={handleConfirm}>Go to Home</GradientButton>
                </Stack>
            </DialogContent>
        </ProfessionalDialog>
    );
}
ServiceRestrictionDialog.propTypes = {
    open: PropTypes.bool,
    serviceKey: PropTypes.string,
    serviceName: PropTypes.string
};
